/**
 * Curated reasoning-model catalog — the SINGLE source of truth for the agent's
 * switchable "brain", shared by three consumers:
 *
 *   1. Settings UI (`src/components/models/ModelSwitcher.tsx`) — picker rows,
 *      per-model effort toggle, plan gating display.
 *   2. Convex (`convex/agentSettings.ts`) — server-side validation + plan
 *      gating before the selection is persisted on the user.
 *   3. Pod render (`domain/claw-config.ts`) — resolves the persisted selection
 *      into `default_model` / `[agent.thinking] default_level`.
 *
 * Pure leaf module: no imports (claw-config imports FROM here — keep it that
 * way or the two create a runtime cycle inside the Convex bundle).
 */

/**
 * zeroclaw `[agent.thinking] default_level` — the `ThinkingLevel` serde enum
 * (`src/agent/thinking.rs`). The binary accepts EXACTLY this set; anything else
 * fails config deserialization at boot and the pod exits 1 (2026-07-24 RCA).
 *
 * DO NOT confuse this with `[runtime] reasoning_effort`, a DIFFERENT config key
 * whose validator accepts `minimal|low|medium|high|xhigh` — the source of the
 * bogus `"xhigh"` that used to sit in this union. That key is provider-side and
 * `providers/compatible.rs` applies it only to `gpt-5*`/`*codex*` models, so it
 * is inert for every OpenRouter model in this catalog. Grepping the binary for
 * a level string cannot tell the two enums apart; pin against the `ThinkingLevel`
 * variants (`MODEL_CATALOG` levels are test-asserted to be a subset).
 *
 * `default_level` is a LOCAL knob: `apply_thinking_level` turns it into
 * temperature / max_tokens adjustments plus a system-prompt prefix. It is NOT
 * sent to OpenRouter as a `reasoning.effort` wire param.
 */
export type ThinkingLevel =
  | "off"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "max";

/**
 * Reasoning effort — a SECOND axis alongside the model choice, stored
 * SEMANTICALLY on the user ("light" | "standard" | "advanced") so each model
 * can map it to ITS OWN thinking level without a data migration. Which efforts
 * a model offers is per-model (`CatalogModel.efforts`) — a curated product
 * decision about how many depths a given brain is worth exposing, NOT a mirror
 * of any provider-published effort set.
 */
export type ReasoningEffort = "light" | "standard" | "advanced";

/** Display copy per effort id — global so the toggle reads consistently
 *  across models; availability + level mapping are per-model. */
export const REASONING_EFFORT_META: Record<
  ReasoningEffort,
  { label: string; blurb: string }
> = {
  light: {
    label: "Light",
    blurb: "Quick answers with minimal deliberation — cheapest.",
  },
  standard: {
    label: "Standard",
    blurb: "Faster, cheaper — good for most tasks.",
  },
  advanced: {
    label: "Advanced",
    blurb: "Thinks longer before acting — uses more tokens.",
  },
};

export interface CatalogModelEffort {
  id: ReasoningEffort;
  /** The zeroclaw thinking level rendered for THIS model at THIS effort — how
   *  hard the agent is told to think locally (prompt prefix + temperature /
   *  max_tokens), not a provider-side reasoning-effort param. MUST be a
   *  `ThinkingLevel` variant or the pod fails to boot. */
  level: ThinkingLevel;
}

/** Qualitative 1–5 capability meters — OUR curated take (stamped
 *  `METERS_AS_OF`), deliberately coarse so we never fake benchmark precision;
 *  the switcher links to LMArena for live numbers. */
export type MeterScore = 1 | 2 | 3 | 4 | 5;

export interface CatalogModelMeters {
  reasoning: MeterScore;
  agentic: MeterScore;
  speed: MeterScore;
}

/** When the curated meters + credit multipliers were last reviewed against
 *  provider pricing/behavior. Shown in the UI next to the meters. */
export const METERS_AS_OF = "Jul 2026";

export interface CatalogModel {
  /** Real OpenRouter model id — rendered into the pod's config.toml. */
  id: string;
  name: string;
  provider: string;
  blurb: string;
  /** The one-line "reach for this when…" — the tile's main copy. */
  bestFor: string;
  /**
   * Credit burn relative to the default model — derived from real OpenRouter
   * per-token pricing (blended for output-heavy agent workloads), NOT vibes.
   * Kimi K3 at $3/$15 per M vs GLM-5.2 at ~$0.78/$2.45 ⇒ ~5×.
   */
  creditMultiplier: number;
  /** Curated capability meters (see METERS_AS_OF). */
  meters: CatalogModelMeters;
  /** Context window in tokens — from OpenRouter's published model config. */
  contextTokens: number;
  /** "included" on every plan; "pro" is plan-gated (BYOK unlocks all). */
  tier: "included" | "pro";
  /**
   * The effort variants THIS model actually supports, in display order —
   * sourced from OpenRouter's published `supported_reasoning_efforts`
   * (checked 2026-07-21). MUST include "standard" (the platform default the
   * clamp falls back to — test-pinned).
   */
  efforts: readonly CatalogModelEffort[];
  /** Optional caution shown on the row — e.g. heavier token/credit burn. */
  warning?: string;
}

export const DEFAULT_REASONING_EFFORT: ReasoningEffort = "standard";

/** External model-comparison leaderboard (crowdsourced side-by-side rankings)
 *  linked from the switcher so users can compare before choosing. */
export const MODEL_COMPARE_URL = "https://arena.ai/leaderboard";

// Ordered light → heavy (ascending capability / credit burn): the picker reads
// as a gradient, cheapest-lightest first. Speed meters run the gradient in
// reverse (Laguna 5 → Kimi 1). NOTE: DEFAULT_REASONING_MODEL_ID (GLM-5.2) is a
// separate constant, NOT MODEL_CATALOG[0] — reordering never moves the default.
export const MODEL_CATALOG: readonly CatalogModel[] = [
  {
    id: "poolside/laguna-s-2.1",
    name: "Laguna S 2.1",
    provider: "Poolside",
    blurb: "Basically free — the ultra-light budget brain for simple work.",
    bestFor:
      "The near-zero-cost floor — an ultralight brain for simple, high-volume tasks.",
    // Real OpenRouter pricing $0.10/$0.20 per M tokens vs GLM's ~$0.78/$2.45
    // ⇒ ~0.1× blended (an 8B-active MoE — tiny inference cost). Basically free;
    // the lightest, least-capable brain in the catalog. There's also an
    // OpenRouter `:free` variant, but this paid id keeps the full 1M context.
    creditMultiplier: 0.1,
    meters: { reasoning: 2, agentic: 2, speed: 5 },
    contextTokens: 1_048_576,
    tier: "included",
    // A lightweight coding model with no graduated reasoning tiers — a single
    // "standard" depth (test-pinned to map to "high", the platform floor).
    efforts: [{ id: "standard", level: "high" }],
  },
  {
    id: "xiaomi/mimo-v2.5-pro",
    name: "MiMo V2.5 Pro",
    provider: "Xiaomi",
    blurb: "Fast, capable everyday work at a fraction of the default's cost.",
    bestFor:
      "The value pick — capable, fast everyday work that stays light on credits.",
    // Real OpenRouter pricing $0.348/$0.696 per M tokens vs GLM's ~$0.78/$2.45
    // ⇒ ~0.3× blended for output-heavy agent runs. Cheaper than the default,
    // trading some reasoning/agentic depth vs GLM-5.2 for the price + speed.
    creditMultiplier: 0.3,
    meters: { reasoning: 3, agentic: 3, speed: 4 },
    contextTokens: 1_048_576,
    tier: "included",
    // Three graduated depths, mirroring Kimi K3's shape. "standard" is present
    // — the test-pinned platform-default floor.
    efforts: [
      { id: "light", level: "low" },
      { id: "standard", level: "high" },
      { id: "advanced", level: "max" },
    ],
  },
  {
    id: "z-ai/glm-5.2",
    name: "GLM-5.2",
    provider: "Z.ai",
    blurb: "Balanced speed and reasoning for everyday work.",
    bestFor: "The balanced daily driver — quick, capable, and easy on credits.",
    creditMultiplier: 1,
    meters: { reasoning: 4, agentic: 4, speed: 3 },
    contextTokens: 1_000_000,
    tier: "included",
    // Two depths only — no "light" tier for the daily driver; "standard" keeps
    // the historical fixed value (high).
    efforts: [
      { id: "standard", level: "high" },
      { id: "advanced", level: "max" },
    ],
  },
  {
    id: "moonshotai/kimi-k3",
    name: "Kimi K3",
    provider: "Moonshot",
    blurb: "Stronger agentic reasoning for complex, multi-step tasks.",
    bestFor:
      "The deep thinker — long multi-step work where getting it right beats getting it fast.",
    // Real pricing gap: $3/$15 per M tokens vs GLM's ~$0.78/$2.45 ⇒ ~5×
    // blended for output-heavy agent runs.
    creditMultiplier: 5,
    meters: { reasoning: 5, agentic: 5, speed: 1 },
    contextTokens: 1_000_000,
    tier: "included",
    // Three graduated depths — the deep thinker earns the full ladder.
    efforts: [
      { id: "light", level: "low" },
      { id: "standard", level: "high" },
      { id: "advanced", level: "max" },
    ],
    warning:
      "Roughly 5× the credit burn of GLM-5.2 — save it for hard problems.",
  },
];

/** "1M" / "256K" — compact context-window formatting for the tile facts row. */
export function formatContextTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${Math.round(tokens / 1_000_000)}M`;
  return `${Math.round(tokens / 1_000)}K`;
}

/** The platform default brain — claw-config's `REASONING_MODEL` aliases this.
 *  Applies only until a user picks: a persisted `reasoningModelId` (settings
 *  switcher) always wins, so editing this constant never re-points an account
 *  that has touched the switcher (model-selection-doctrine §3.8). */
export const DEFAULT_REASONING_MODEL_ID = "z-ai/glm-5.2";

export function isCatalogModelId(id: string): boolean {
  return MODEL_CATALOG.some((m) => m.id === id);
}

export function modelById(id: string): CatalogModel {
  return MODEL_CATALOG.find((m) => m.id === id) ?? MODEL_CATALOG[0];
}

export function isEffortSupported(
  model: CatalogModel,
  effort: ReasoningEffort,
): boolean {
  return model.efforts.some((e) => e.id === effort);
}

/**
 * Total resolver: the effort actually in force for a (model, effort) pair —
 * an absent or model-unsupported effort falls back to the platform default
 * (every catalog model supports "standard", test-pinned). Used by the UI when
 * switching models AND by the render, so the two can never disagree.
 */
export function clampEffort({
  model,
  effort,
}: {
  model: CatalogModel;
  effort: ReasoningEffort | undefined;
}): ReasoningEffort {
  if (effort && isEffortSupported(model, effort)) return effort;
  return DEFAULT_REASONING_EFFORT;
}

/** The zeroclaw thinking level to render for a (model, effort) pair. Total —
 *  clamps internally. */
export function thinkingLevelFor({
  model,
  effort,
}: {
  model: CatalogModel;
  effort: ReasoningEffort | undefined;
}): ThinkingLevel {
  const effective = clampEffort({ model, effort });
  const entry = model.efforts.find((e) => e.id === effective);
  // Unreachable while every model carries "standard" (test-pinned); the
  // historical fixed value is the safe floor.
  return entry?.level ?? "high";
}

/**
 * Plan gate, enforced BOTH in the UI (lock icon + upgrade dialog) and in the
 * `setReasoningConfig` mutation (the UI check alone is decoration). `plan` is
 * structurally `Plan` (claw-config) — typed as the literal union to keep this
 * module a leaf.
 */
export function isModelLockedForPlan(
  model: CatalogModel,
  plan: "trial" | "byok" | "standard",
): boolean {
  return model.tier === "pro" && plan !== "byok";
}
